import { FC, useMemo } from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { mdiUnfoldMoreHorizontal } from '@mdi/js';
import Icon from '@mdi/react';
import './RepeatSelect.scss';
import { formatDataDate } from '../../DataView/i18n';

interface RepeatSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  dueDateTime?: {
    datetime?: string;
    date?: string;
    timezone?: string;
  };
  onDateChange?: (dateSet: { date?: string; datetime?: string }) => void;
  disabled?: boolean;
  className?: string;
}

interface RepeatOption {
  value: string | null;
  label: string;
}

// Strip time from value if present (for backward compatibility with old data)
const getValueWithoutTime = (val: string | null): string | null => {
  if (!val) return null;
  return val.replace(/\s+at\s+\d{1,2}:\d{2}\s*(am|pm)?/i, '').trim();
};

export const RepeatSelect: FC<RepeatSelectProps> = ({
  value,
  onChange,
  dueDateTime,
  onDateChange,
  disabled,
  className,
}) => {
  const { t, i18n } = useTranslation();
  // Get current date info using dayjs
  const currentDate = dueDateTime?.date
    ? dayjs(dueDateTime.date)
    : dueDateTime?.datetime
      ? dayjs(dueDateTime.datetime)
      : dayjs();

  const currentDateForRecurrenceValue = currentDate.locale('en');
  const dayOfWeek = currentDateForRecurrenceValue.format('ddd').toLowerCase(); // Short day name (e.g., "mon")
  const dayOfMonthWithOrdinal = currentDateForRecurrenceValue.format('Do'); // e.g., "13th"
  const monthShort = currentDateForRecurrenceValue.format('MMM').toLowerCase(); // Short month name in lowercase
  const dayOfWeekFull = formatDataDate(i18n.language, currentDate.toDate(), {
    weekday: 'long',
  });
  const dayOfMonthLabel =
    i18n.language === 'en'
      ? dayOfMonthWithOrdinal
      : formatDataDate(i18n.language, currentDate.toDate(), { day: 'numeric' });
  const monthName = formatDataDate(i18n.language, currentDate.toDate(), {
    month: 'long',
  });

  // Check if current date is a weekday (Mon=1 to Fri=5, Sat=6, Sun=0)
  const dayNumber = currentDate.day();
  const isWeekday = dayNumber >= 1 && dayNumber <= 5;

  const normalizedValue = getValueWithoutTime(value);

  const predefinedOptions: RepeatOption[] = useMemo(
    () => [
      { value: 'every day', label: t('task.repeat.everyDay') },
      {
        value: `every ${dayOfWeek}`,
        label: t('task.repeat.everyWeekOn', { weekday: dayOfWeekFull }),
      },
      { value: 'every weekday', label: t('task.repeat.everyWeekday') },
      {
        value: `every ${dayOfMonthWithOrdinal}`,
        label: t('task.repeat.everyMonthOn', { day: dayOfMonthLabel }),
      },
      {
        value: `every ${dayOfMonthWithOrdinal} ${monthShort}`,
        label: t('task.repeat.everyYearOn', { month: monthName, day: dayOfMonthLabel }),
      },
    ],
    [dayOfMonthLabel, dayOfMonthWithOrdinal, dayOfWeek, dayOfWeekFull, monthName, monthShort, t]
  );

  // Check if current value is a custom recurrence (not in predefined options)
  const isCustomRecurrence = useMemo(() => {
    if (!normalizedValue) return false;
    return !predefinedOptions.some((opt) => opt.value === normalizedValue);
  }, [normalizedValue, predefinedOptions]);

  // Build final options list, adding custom option if needed
  const options: RepeatOption[] = useMemo(() => {
    if (isCustomRecurrence && normalizedValue) {
      return [{ value: normalizedValue, label: startCase(normalizedValue) }, ...predefinedOptions];
    }
    return predefinedOptions;
  }, [predefinedOptions, isCustomRecurrence, normalizedValue]);

  const handleChange = (e: SelectChangeEvent<string>) => {
    const newValue = e.target.value || null;

    // If selecting weekdays and it's a weekend, move to next Monday
    if (newValue === 'every weekday' && !isWeekday && onDateChange) {
      const daysUntilMonday = dayNumber === 0 ? 1 : 8 - dayNumber;
      const nextMonday = currentDate.add(daysUntilMonday, 'day');

      if (dueDateTime?.datetime) {
        onDateChange({
          datetime: nextMonday.toISOString(),
          date: nextMonday.format('YYYY-MM-DD'),
        });
      } else {
        onDateChange({
          date: nextMonday.format('YYYY-MM-DD'),
        });
      }
    }

    onChange(newValue);
  };

  const getDisplayLabel = (): string => {
    if (!normalizedValue) {
      return t('task.repeat.dontRepeat');
    }
    return (
      options.find((option) => option.value === normalizedValue)?.label ??
      startCase(normalizedValue)
    );
  };

  return (
    <Select
      className={clsx('repeat-select', className)}
      size="small"
      value={normalizedValue ?? ''}
      onChange={handleChange}
      displayEmpty
      disabled={disabled}
      IconComponent={() => (
        <Icon path={mdiUnfoldMoreHorizontal} size="16px" className="repeat-select__icon" />
      )}
      renderValue={() => {
        const isEmpty = !normalizedValue;
        return (
          <div className={clsx('repeat-select__value', isEmpty && 'repeat-select__value--empty')}>
            <span>{getDisplayLabel()}</span>
          </div>
        );
      }}
      MenuProps={{
        PaperProps: {
          className: 'repeat-select__menu-paper',
        },
      }}
    >
      {options.map((opt) => (
        <MenuItem
          key={opt.value ?? 'none'}
          value={opt.value ?? ''}
          className={clsx(
            'repeat-select__item',
            normalizedValue === opt.value && 'repeat-select__item--selected',
            isCustomRecurrence && opt.value === normalizedValue && 'repeat-select__item--custom'
          )}
        >
          <span className="repeat-select__item__label">{opt.label}</span>
        </MenuItem>
      ))}
      <MenuItem value="" className="repeat-select__item repeat-select__item--none">
        <span className="repeat-select__item__label">{t('task.repeat.dontRepeat')}</span>
      </MenuItem>
    </Select>
  );
};

export default RepeatSelect;
